import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import type { PaginatedResponse, PlayEvent, PlayEventParticipant } from '@court-sync/shared';
import {
  canCheckInPlayEventParticipant,
  canRemovePlayEventParticipantFromRoster,
  canWithdrawPlayEventParticipantFromRoster,
  getPlayEventParticipantDisplayName,
  isPlayEventParticipantRosterSelectable,
  resolvePlayEventPrice,
} from '@court-sync/shared';
import toast from 'react-hot-toast';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PaginatedTable,
  type PaginatedTableColumn,
  type PaginationState,
} from '@/components/ui';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { playEventService } from '@/services/playEvent';
import {
  getPlayParticipantPaymentBadgeVariant,
  getPlayParticipantPaymentLabel,
  PLAY_PARTICIPANT_STATUS_BADGE_VARIANT,
  PLAY_PARTICIPANT_STATUS_LABELS,
} from '@/lib/playEventLabels';
import { ChevronDown, MoreVertical, Plus, Search, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 20;

type RosterRemovalMode = 'remove' | 'withdraw';

function isPaginatedResponse(
  res: { success: boolean; data?: PlayEventParticipant[]; pagination?: PaginationState }
): res is PaginatedResponse<PlayEventParticipant> {
  return res.success && res.pagination != null;
}

function isParticipantPaymentCollectable(
  event: PlayEvent,
  participant: PlayEventParticipant
): boolean {
  if (participant.paymentStatus !== 'unpaid') return false;
  const price = resolvePlayEventPrice({
    event,
    participantType: participant.participantType,
  });
  return price.finalPriceCents > 0;
}

export interface PlayEventRosterPanelHandle {
  reload: () => void;
}

interface PlayEventRosterPanelProps {
  organizationId: string;
  eventId: string;
  event: PlayEvent;
  canManageRoster: boolean;
  rosterRemovalMode: RosterRemovalMode;
  showManualPaymentAlert: boolean;
  onAddParticipant: () => void;
  onParticipantsChanged: () => void;
}

export const PlayEventRosterPanel = forwardRef<
  PlayEventRosterPanelHandle,
  PlayEventRosterPanelProps
>(function PlayEventRosterPanel(
  {
    organizationId,
    eventId,
    event,
    canManageRoster,
    rosterRemovalMode,
    showManualPaymentAlert,
    onAddParticipant,
    onParticipantsChanged,
  },
  ref
) {
  const rosterRemovalVerb = rosterRemovalMode === 'remove' ? 'Remove' : 'Withdraw';
  const [rows, setRows] = useState<PlayEventParticipant[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState<
    Map<string, PlayEventParticipant>
  >(new Map());
  const [rosterActionLoading, setRosterActionLoading] = useState(false);
  const [checkingInParticipantId, setCheckingInParticipantId] = useState<string | null>(null);
  const [rosterRemovalDialogOpen, setRosterRemovalDialogOpen] = useState(false);
  const [pendingRosterRemoval, setPendingRosterRemoval] = useState<{
    mode: RosterRemovalMode;
    participantIds: string[];
  } | null>(null);

  const loadPage = useCallback(
    async (nextPage: number) => {
      setLoading(true);
      try {
        const res = await playEventService.listParticipants(organizationId, eventId, {
          page: nextPage,
          limit: PAGE_SIZE,
          ...(searchQuery ? { search: searchQuery } : {}),
        });
        if (isPaginatedResponse(res)) {
          setRows(res.data ?? []);
          setPagination(res.pagination);
        } else {
          setRows([]);
          setPagination(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [eventId, organizationId, searchQuery]
  );

  useImperativeHandle(ref, () => ({ reload: () => void loadPage(page) }), [loadPage, page]);

  useEffect(() => {
    void loadPage(page);
  }, [loadPage, page]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedParticipants((previous) => {
      const next = new Map(previous);
      for (const participant of rows) {
        if (next.has(participant.id)) {
          next.set(participant.id, participant);
        }
      }
      return next;
    });
  }, [rows]);

  const selectedParticipantIds = useMemo(
    () => new Set(selectedParticipants.keys()),
    [selectedParticipants]
  );

  const pageSelectableParticipants = useMemo(
    () => rows.filter(isPlayEventParticipantRosterSelectable),
    [rows]
  );

  const selectedRegisteredCount = useMemo(
    () =>
      [...selectedParticipants.values()].filter((participant) => participant.status === 'registered')
        .length,
    [selectedParticipants]
  );

  const selectedRemovableCount = useMemo(
    () =>
      [...selectedParticipants.values()].filter((participant) =>
        rosterRemovalMode === 'remove'
          ? canRemovePlayEventParticipantFromRoster(event, participant)
          : canWithdrawPlayEventParticipantFromRoster(event, participant)
      ).length,
    [event, rosterRemovalMode, selectedParticipants]
  );

  const selectedPayableCount = useMemo(
    () =>
      [...selectedParticipants.values()].filter((participant) =>
        isParticipantPaymentCollectable(event, participant)
      ).length,
    [event, selectedParticipants]
  );

  const clearParticipantSelection = useCallback(() => setSelectedParticipants(new Map()), []);

  const toggleParticipantSelect = useCallback((participant: PlayEventParticipant) => {
    setSelectedParticipants((previous) => {
      const next = new Map(previous);
      if (next.has(participant.id)) {
        next.delete(participant.id);
      } else {
        next.set(participant.id, participant);
      }
      return next;
    });
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    const pageIds = new Set(pageSelectableParticipants.map((participant) => participant.id));
    const allPageSelected =
      pageSelectableParticipants.length > 0 &&
      pageSelectableParticipants.every((participant) => selectedParticipants.has(participant.id));

    setSelectedParticipants((previous) => {
      const next = new Map(previous);
      if (allPageSelected) {
        for (const id of pageIds) {
          next.delete(id);
        }
        return next;
      }
      for (const participant of pageSelectableParticipants) {
        next.set(participant.id, participant);
      }
      return next;
    });
  }, [pageSelectableParticipants, selectedParticipants]);

  const openRosterRemovalDialog = useCallback(
    (mode: RosterRemovalMode, participantIds: string[]) => {
      setPendingRosterRemoval({ mode, participantIds });
      setRosterRemovalDialogOpen(true);
    },
    []
  );

  const refreshRoster = useCallback(async () => {
    await loadPage(page);
    onParticipantsChanged();
  }, [loadPage, onParticipantsChanged, page]);

  const handleCheckIn = async (participantId: string) => {
    setCheckingInParticipantId(participantId);
    try {
      const res = await playEventService.checkInParticipant(
        organizationId,
        eventId,
        participantId
      );
      if (res.success) {
        toast.success('Checked in');
        clearParticipantSelection();
        await refreshRoster();
      } else {
        toast.error(res.error ?? 'Failed to check in');
      }
    } finally {
      setCheckingInParticipantId(null);
    }
  };

  const handleBulkCheckIn = async () => {
    const participantIds = [...selectedParticipants.values()]
      .filter((participant) => participant.status === 'registered')
      .map((participant) => participant.id);
    if (participantIds.length === 0) {
      toast.error('Select registered participants to check in');
      return;
    }

    setRosterActionLoading(true);
    let successCount = 0;
    let failureCount = 0;
    try {
      for (const participantId of participantIds) {
        const res = await playEventService.checkInParticipant(
          organizationId,
          eventId,
          participantId
        );
        if (res.success) {
          successCount += 1;
        } else {
          failureCount += 1;
        }
      }
      if (successCount > 0) {
        toast.success(`Checked in ${successCount} participant${successCount === 1 ? '' : 's'}`);
        clearParticipantSelection();
        await refreshRoster();
      }
      if (failureCount > 0) {
        toast.error(
          `Failed to check in ${failureCount} participant${failureCount === 1 ? '' : 's'}`
        );
      }
    } finally {
      setRosterActionLoading(false);
    }
  };

  const handleBulkRosterRemoval = async () => {
    if (!pendingRosterRemoval) return;
    const participantIds = pendingRosterRemoval.participantIds.filter((id) => {
      const participant = selectedParticipants.get(id) ?? rows.find((row) => row.id === id);
      if (!participant) return false;
      return pendingRosterRemoval.mode === 'remove'
        ? canRemovePlayEventParticipantFromRoster(event, participant)
        : canWithdrawPlayEventParticipantFromRoster(event, participant);
    });
    if (participantIds.length === 0) {
      toast.error(`Select participants to ${pendingRosterRemoval.mode}`);
      return;
    }

    setRosterActionLoading(true);
    let successCount = 0;
    let failureCount = 0;
    const action =
      pendingRosterRemoval.mode === 'remove'
        ? playEventService.removeParticipant.bind(playEventService)
        : playEventService.withdrawParticipant.bind(playEventService);
    try {
      for (const participantId of participantIds) {
        const res = await action(organizationId, eventId, participantId);
        if (res.success) {
          successCount += 1;
        } else {
          failureCount += 1;
        }
      }
      if (successCount > 0) {
        const pastTense = pendingRosterRemoval.mode === 'remove' ? 'Removed' : 'Withdrew';
        toast.success(`${pastTense} ${successCount} participant${successCount === 1 ? '' : 's'}`);
        clearParticipantSelection();
        await refreshRoster();
      }
      if (failureCount > 0) {
        toast.error(
          `Failed to ${pendingRosterRemoval.mode} ${failureCount} participant${failureCount === 1 ? '' : 's'}`
        );
      }
    } finally {
      setRosterActionLoading(false);
      setRosterRemovalDialogOpen(false);
      setPendingRosterRemoval(null);
    }
  };

  const handleRecordDoorPayment = async (
    participantId: string,
    method: 'cash' | 'eftpos' | 'comped'
  ) => {
    setRosterActionLoading(true);
    try {
      const res = await playEventService.recordDoorPayment(
        organizationId,
        eventId,
        participantId,
        method
      );
      if (res.success) {
        const label =
          method === 'comped'
            ? 'Marked as complimentary'
            : method === 'cash'
              ? 'Marked as paid (cash)'
              : 'Marked as paid (EFTPOS)';
        toast.success(label);
        await refreshRoster();
      } else {
        toast.error(res.error ?? 'Failed to record payment');
      }
    } finally {
      setRosterActionLoading(false);
    }
  };

  const handleBulkRecordDoorPayment = async (method: 'cash' | 'eftpos' | 'comped') => {
    const participantIds = [...selectedParticipants.values()]
      .filter((participant) => isParticipantPaymentCollectable(event, participant))
      .map((participant) => participant.id);
    if (participantIds.length === 0) {
      toast.error('Select unpaid participants with a fee to mark as paid');
      return;
    }

    setRosterActionLoading(true);
    let successCount = 0;
    let failureCount = 0;
    try {
      for (const participantId of participantIds) {
        const res = await playEventService.recordDoorPayment(
          organizationId,
          eventId,
          participantId,
          method
        );
        if (res.success) {
          successCount += 1;
        } else {
          failureCount += 1;
        }
      }
      if (successCount > 0) {
        const label =
          method === 'comped'
            ? `Marked ${successCount} participant${successCount === 1 ? '' : 's'} as complimentary`
            : method === 'cash'
              ? `Marked ${successCount} participant${successCount === 1 ? '' : 's'} as paid (cash)`
              : `Marked ${successCount} participant${successCount === 1 ? '' : 's'} as paid (EFTPOS)`;
        toast.success(label);
        clearParticipantSelection();
        await refreshRoster();
      }
      if (failureCount > 0) {
        toast.error(
          `Failed to update payment for ${failureCount} participant${failureCount === 1 ? '' : 's'}`
        );
      }
    } finally {
      setRosterActionLoading(false);
    }
  };

  const columns: PaginatedTableColumn<PlayEventParticipant>[] = useMemo(() => {
    const allPageSelected =
      pageSelectableParticipants.length > 0 &&
      pageSelectableParticipants.every((participant) => selectedParticipants.has(participant.id));

    const baseColumns: PaginatedTableColumn<PlayEventParticipant>[] = [
      {
        key: 'name',
        header: 'Name',
        render: (participant) => getPlayEventParticipantDisplayName(participant),
      },
      {
        key: 'type',
        header: 'Type',
        render: (participant) => participant.participantType,
      },
      {
        key: 'status',
        header: 'Status',
        render: (participant) => (
          <Badge variant={PLAY_PARTICIPANT_STATUS_BADGE_VARIANT[participant.status]}>
            {PLAY_PARTICIPANT_STATUS_LABELS[participant.status]}
          </Badge>
        ),
      },
      {
        key: 'payment',
        header: 'Payment',
        render: (participant) => (
          <Badge variant={getPlayParticipantPaymentBadgeVariant(event, participant)}>
            {getPlayParticipantPaymentLabel(event, participant)}
          </Badge>
        ),
      },
    ];

    if (!canManageRoster) {
      return baseColumns;
    }

    return [
      {
        key: 'select',
        header: (
          <Checkbox
            checked={allPageSelected}
            onCheckedChange={toggleSelectAllOnPage}
            disabled={pageSelectableParticipants.length === 0 || rosterActionLoading}
            aria-label="Select all participants on this page"
          />
        ),
        className: 'w-10',
        render: (participant) => {
          const selectable = isPlayEventParticipantRosterSelectable(participant);
          return (
            <Checkbox
              checked={selectedParticipantIds.has(participant.id)}
              onCheckedChange={() => toggleParticipantSelect(participant)}
              disabled={!selectable || rosterActionLoading}
              aria-label={`Select ${getPlayEventParticipantDisplayName(participant)}`}
              onClick={(e) => e.stopPropagation()}
            />
          );
        },
      },
      ...baseColumns,
      {
        key: 'actions',
        header: '',
        className: 'w-12 text-right',
        render: (participant) => {
          const canCheckIn = canCheckInPlayEventParticipant(event, participant);
          const canRemove = canRemovePlayEventParticipantFromRoster(event, participant);
          const canWithdraw = canWithdrawPlayEventParticipantFromRoster(event, participant);
          const canCollectPayment = isParticipantPaymentCollectable(event, participant);
          const hasRowActions = canCheckIn || canRemove || canWithdraw || canCollectPayment;

          if (!hasRowActions) {
            return <span className="text-sm text-muted-foreground">—</span>;
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={rosterActionLoading}
                  aria-label={`Actions for ${getPlayEventParticipantDisplayName(participant)}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canCheckIn ? (
                  <DropdownMenuItem
                    disabled={checkingInParticipantId === participant.id}
                    onSelect={() => void handleCheckIn(participant.id)}
                  >
                    Check in
                  </DropdownMenuItem>
                ) : null}
                {canRemove ? (
                  <>
                    {canCheckIn ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() => openRosterRemovalDialog('remove', [participant.id])}
                    >
                      Remove
                    </DropdownMenuItem>
                  </>
                ) : null}
                {canWithdraw ? (
                  <>
                    {canCheckIn || canRemove ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() => openRosterRemovalDialog('withdraw', [participant.id])}
                    >
                      Withdraw
                    </DropdownMenuItem>
                  </>
                ) : null}
                {canCollectPayment ? (
                  <>
                    {canCheckIn || canRemove || canWithdraw ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      disabled={rosterActionLoading}
                      onSelect={() => void handleRecordDoorPayment(participant.id, 'cash')}
                    >
                      Mark paid (cash)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={rosterActionLoading}
                      onSelect={() => void handleRecordDoorPayment(participant.id, 'eftpos')}
                    >
                      Mark paid (EFTPOS)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={rosterActionLoading}
                      onSelect={() => void handleRecordDoorPayment(participant.id, 'comped')}
                    >
                      Mark complimentary
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ];
  }, [
    canManageRoster,
    checkingInParticipantId,
    event,
    openRosterRemovalDialog,
    pageSelectableParticipants,
    rosterActionLoading,
    selectedParticipantIds,
    selectedParticipants,
    toggleParticipantSelect,
    toggleSelectAllOnPage,
  ]);

  const totalCount = pagination?.total ?? rows.length;

  return (
    <>
      {showManualPaymentAlert ? (
        <Alert variant="warning" className="mb-4">
          <TriangleAlert className="size-4" aria-hidden />
          <AlertTitle>Manual payment collection</AlertTitle>
          <AlertDescription>
            Online payment collection is not enabled.{' '}
            <Link
              to="/admin/payments?tab=settings"
              className="font-medium underline underline-offset-4 hover:opacity-90"
            >
              Enable online payments
            </Link>
            , or mark payments from each participant&apos;s row actions after you collect payment.
            Reconcile fees in{' '}
            <Link
              to="/admin/payments?tab=student-billing"
              className="font-medium underline underline-offset-4 hover:opacity-90"
            >
              Billing Report
            </Link>
            .
          </AlertDescription>
        </Alert>
      ) : null}

      <DashboardDataCard
        title="Participants"
        headerAction={
          canManageRoster ? (
            <Button type="button" size="sm" onClick={onAddParticipant}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              Add participant
            </Button>
          ) : undefined
        }
        toolbar={
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <p className="text-sm text-muted-foreground">
                {loading ? 'Loading participants…' : `${totalCount} participant${totalCount === 1 ? '' : 's'}`}
              </p>
              {canManageRoster ? (
                <span className="text-sm text-muted-foreground">
                  {selectedParticipantIds.size > 0
                    ? `${selectedParticipantIds.size} selected`
                    : `Select rows to check in, mark paid, or ${rosterRemovalMode} participants.`}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-64">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  type="search"
                  placeholder="Search by name…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                  aria-label="Search participants by name"
                />
              </div>
              {canManageRoster ? (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={selectedParticipantIds.size === 0 || rosterActionLoading}
                        className="flex items-center gap-1.5"
                        aria-label="Bulk actions for selected participants"
                      >
                        Bulk actions
                        <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[12rem]">
                      <DropdownMenuItem
                        disabled={selectedRegisteredCount === 0}
                        onSelect={() => void handleBulkCheckIn()}
                      >
                        Check in selected
                        {selectedRegisteredCount > 0 ? ` (${selectedRegisteredCount})` : ''}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        disabled={selectedPayableCount === 0}
                        onSelect={() => void handleBulkRecordDoorPayment('cash')}
                      >
                        Mark paid (cash)
                        {selectedPayableCount > 0 ? ` (${selectedPayableCount})` : ''}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={selectedPayableCount === 0}
                        onSelect={() => void handleBulkRecordDoorPayment('eftpos')}
                      >
                        Mark paid (EFTPOS)
                        {selectedPayableCount > 0 ? ` (${selectedPayableCount})` : ''}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={selectedPayableCount === 0}
                        onSelect={() => void handleBulkRecordDoorPayment('comped')}
                      >
                        Mark complimentary
                        {selectedPayableCount > 0 ? ` (${selectedPayableCount})` : ''}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        disabled={selectedRemovableCount === 0}
                        onSelect={() =>
                          openRosterRemovalDialog(rosterRemovalMode, [...selectedParticipantIds])
                        }
                      >
                        {rosterRemovalVerb} selected
                        {selectedRemovableCount > 0 ? ` (${selectedRemovableCount})` : ''}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={selectedParticipantIds.size === 0 || rosterActionLoading}
                    onClick={clearParticipantSelection}
                  >
                    Deselect all
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        }
      >
        <PaginatedTable
          columns={columns}
          data={rows}
          loading={loading}
          pagination={pagination}
          onPageChange={setPage}
          emptyMessage={
            searchQuery ? 'No participants match your search.' : 'No participants on this page.'
          }
          noWrapperPadding
          mobileTitleColumnKey="name"
        />
      </DashboardDataCard>

      <AlertDialog
        open={rosterRemovalDialogOpen}
        onOpenChange={(open) => {
          if (!rosterActionLoading) {
            setRosterRemovalDialogOpen(open);
            if (!open) setPendingRosterRemoval(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingRosterRemoval?.mode === 'remove' ? 'Remove participants?' : 'Withdraw participants?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRosterRemoval?.participantIds.length ?? selectedRemovableCount} participant
              {(pendingRosterRemoval?.participantIds.length ?? selectedRemovableCount) === 1
                ? ''
                : 's'}{' '}
              will be affected.
              {pendingRosterRemoval?.mode === 'remove'
                ? ' Selected players will be permanently removed from this draft event roster.'
                : ' Selected players will be withdrawn from this event.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={rosterActionLoading}>
              Keep on roster
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={rosterActionLoading}
              isLoading={rosterActionLoading}
              onClick={() => void handleBulkRosterRemoval()}
            >
              {pendingRosterRemoval?.mode === 'remove' ? 'Remove' : 'Withdraw'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});
